import {
	Box,
	Button,
	CircularProgress,
	Container,
	IconButton,
	InputAdornment,
	Link,
	TextField,
	Typography,
	Paper,
	Fade
} from "@mui/material";
import { FieldValues, useForm } from "react-hook-form";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ReplayIcon from "@mui/icons-material/Replay";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import "./login.css";
import { useAppDispatch, useAppSelector } from "../../redux";
import { loginUser } from "../../redux/reducers/auth.reducer";
// import { ReactComponent as GateGuardLogo } from "../../assets/logos/gate-guard-logo.svg";
import { ReactComponent as GGLoginImage } from "../../assets/images/gg-login.svg";
import ForgotPasswordDialog from "./ForgotPasswordDialog";
import Captcha, { ICaptchaRef } from "./Captcha";
import { ReactComponent as ServiceOfflineImage } from "../../assets/images/service-offline.svg";
import { BASE_API_URL } from "../../utils/apiUtils";
import { DEPLOYMENT_ERROR_MESSAGE, SYSTEM_INFO_MISSING_ERROR_MESSAGE } from "../../utils/constants";
import { useCheckOtsStatusQuery } from "../../redux/reducers/settings.reducer";

const Login: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const authState = useAppSelector((state) => state.auth);

	const captchaRef = useRef<ICaptchaRef>(null);
	const loadingTimer = useRef<NodeJS.Timeout | null>(null);

	const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState<boolean>(false);
	const [showServiceStartingMessage, setShowServiceStartingMessage] = useState<boolean>(false);
	const [showLoader, setShowLoader] = useState<boolean>(true);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [retryMessage, setRetryMessage] = useState<string>("");

	// APIS
	const {
		data: siteConfig,
		isLoading: getSiteConfigLoading,
		isFetching: getSiteConfigFetching,
		error: getSiteConfigError,
		refetch: refetchSiteConfig
	} = useCheckOtsStatusQuery();

	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm();

	function handleOpenForgotPasswordDialog() {
		setShowForgotPasswordDialog(true);
	}

	function handleCloseForgotPasswordDialog() {
		setShowForgotPasswordDialog(false);
	}

	function handleLoginFormSubmit(data: FieldValues) {
		if (captchaRef.current && captchaRef.current.checkCaptcha()) {
			dispatch(loginUser({ username: data.username, password: data.password }));
		}
	}

	function handleTogglePasswordVisibility() {
		setShowPassword(!showPassword);
	}

	const checkAuthNavigate = useCallback(() => {
		if (authState.user.token && authState.user.token !== "invalid") {
			navigate("/dashboard");
			return;
		}

		if (!siteConfig) {
			return;
		}

		if (loadingTimer.current) {
			clearTimeout(loadingTimer.current);
			loadingTimer.current = null;
			setShowServiceStartingMessage(false);
		}

		setShowLoader(false);
	}, [authState.user.token, navigate, siteConfig]);

	function handleRetry() {
		setRetryMessage("");
		setShowServiceStartingMessage(false);
		refetchSiteConfig();
	}

	useEffect(() => {
		let faviconLink = document.querySelector("link[rel~='icon']");

		if (!faviconLink) {
			faviconLink = document.createElement("link");
			faviconLink.setAttribute("rel", "icon");
			document.getElementsByTagName("head")[0].appendChild(faviconLink);
		}

		faviconLink.setAttribute("href", `${BASE_API_URL}/media/icons/favicon.ico`);
	}, []);

	useEffect(() => {
		checkAuthNavigate();
	}, [checkAuthNavigate]);

	useEffect(() => {
		if (authState.currentAction === "auth/loginUser" && authState.error && captchaRef.current) {
			captchaRef.current.resetCaptcha();
		}
	}, [authState.currentAction, authState.error]);

	useEffect(() => {
		if (getSiteConfigLoading || getSiteConfigFetching) {
			if (!loadingTimer.current) {
				loadingTimer.current = setTimeout(() => {
					setShowServiceStartingMessage(true);
					loadingTimer.current = null;
				}, 5 * 1000);
			}
		}
	}, [getSiteConfigFetching, getSiteConfigLoading]);

	useEffect(() => {
		if (getSiteConfigError) {
			if ("status" in getSiteConfigError && getSiteConfigError.status === 401) {
				setShowLoader(false);
			} else if ("data" in getSiteConfigError && getSiteConfigError.data === DEPLOYMENT_ERROR_MESSAGE) {
				setRetryMessage(DEPLOYMENT_ERROR_MESSAGE);
			} else if ("data" in getSiteConfigError && getSiteConfigError.data === SYSTEM_INFO_MISSING_ERROR_MESSAGE) {
				setRetryMessage(SYSTEM_INFO_MISSING_ERROR_MESSAGE);
			} else {
				setTimeout(() => {
					refetchSiteConfig();
				}, 5 * 1000);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getSiteConfigError]);

	if (showLoader) {
		if (retryMessage) {
			return (
				<Box className="service-offline-message-wrapper">
					<Box sx={{ width: { xs: "280px", sm: "350px", md: "400px" } }}>
						<ServiceOfflineImage width="100%" />
					</Box>

					<Typography
						variant="h5"
						sx={{
							textAlign: "center",
							fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
							px: { xs: 2, sm: 3 }
						}}
					>
						{retryMessage}
					</Typography>

					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<ReplayIcon />}
						onClick={handleRetry}
						sx={{
							background: "#667eea",
							borderRadius: "12px",
							px: { xs: 2, sm: 3 },
							py: { xs: 1, sm: 1.5 },
							textTransform: "none",
							fontWeight: 600,
							fontSize: { xs: "0.875rem", sm: "1rem" },
							boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
							"&:hover": {
								background: "#5a6fd8",
								boxShadow: "0 12px 40px rgba(102, 126, 234, 0.4)"
							}
						}}
					>
						Retry
					</Button>
				</Box>
			);
		}

		if (showServiceStartingMessage) {
			return (
				<Box className="service-offline-message-wrapper">
					<Box sx={{ width: { xs: "280px", sm: "350px", md: "400px" } }}>
						<ServiceOfflineImage width="100%" />
					</Box>

					<CircularProgress sx={{ color: "#667eea" }} />

					<Typography
						variant="h5"
						sx={{
							textAlign: "center",
							fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
							px: { xs: 2, sm: 3 }
						}}
					>
						Please wait, the login page will be ready once the backend services are up.
					</Typography>
				</Box>
			);
		}

		return (
			<Box className="check-login-loader-wrapper">
				<CircularProgress sx={{ color: "#667eea" }} />
			</Box>
		);
	}

	return (
		<Box className="login-component-wrapper">
			<Container maxWidth="xl" sx={{ height: "100%" }}>
				<Box className="login-content-wrapper">
					<Box className="login-image-section">
						{/* {process.env.REACT_APP_HIDE_GG_LOGO !== "true" ? <GateGuardLogo width="90%" /> : null} */}

						{process.env.REACT_APP_HIDE_GG_LOGO !== "true" ? (
							<Box sx={{ width: { xs: "80%", sm: "85%", md: "90%" } }}>
								<img src={`${BASE_API_URL}/media/icons/logo.png`} alt={"Logo"} width="100%" />
							</Box>
						) : null}

						<Box sx={{ width: { xs: "80%", sm: "85%", md: "90%" } }}>
							<GGLoginImage width="100%" />
						</Box>
					</Box>

					<Box className="login-form-section">
						<Fade in timeout={800}>
							<Paper
								elevation={0}
								sx={{
									background: "rgba(102, 126, 234, 0.08)",
									borderRadius: "24px",
									p: { xs: 2, sm: 3, md: 4 },
									width: "100%",
									maxWidth: { xs: "100%", sm: "480px" },
									border: "1px solid rgba(102, 126, 234, 0.15)",
									boxShadow: "0 8px 32px rgba(102, 126, 234, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
									mb: { xs: 2, sm: 0 }
								}}
							>
								<Box sx={{ textAlign: "center", mb: { xs: 2, sm: 3, md: 4 } }}>
									<Box
										sx={{
											width: { xs: 48, sm: 56, md: 64 },
											height: { xs: 48, sm: 56, md: 64 },
											borderRadius: "50%",
											background: "#667eea",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											mx: "auto",
											mb: { xs: 1.5, sm: 2 },
											boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)"
										}}
									>
										<LockOutlinedIcon sx={{ color: "white", fontSize: { xs: 24, sm: 28, md: 32 } }} />
									</Box>

									<Typography
										variant="h4"
										sx={{
											fontWeight: 700,
											color: "#667eea",
											mb: 1,
											fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" }
										}}
									>
										Welcome Back
									</Typography>

									<Typography
										variant="body1"
										color="text.secondary"
										sx={{
											fontWeight: 500,
											fontSize: { xs: "0.875rem", sm: "1rem" }
										}}
									>
										Enter your credentials to access your account
									</Typography>
								</Box>

								<Box
									component="form"
									noValidate
									onSubmit={handleSubmit(handleLoginFormSubmit)}
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: { xs: 2, sm: 2.5, md: 3 },
										mb: { xs: 1, sm: 0 }
									}}
								>
									<Box>
										<Typography
											variant="body2"
											fontWeight={600}
											color="text.primary"
											sx={{ mb: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
										>
											Username
										</Typography>

										<TextField
											required
											fullWidth
											variant="outlined"
											placeholder="Enter your username"
											autoComplete="off"
											InputProps={{
												sx: {
													borderRadius: "12px",
													backgroundColor: "rgba(102, 126, 234, 0.05)",
													"&:hover": {
														backgroundColor: "rgba(102, 126, 234, 0.08)"
													},
													"&.Mui-focused": {
														backgroundColor: "rgba(102, 126, 234, 0.1)"
													},
													fontSize: { xs: "0.875rem", sm: "1rem" }
												}
											}}
											error={!!errors.username}
											helperText={errors.username && errors.username.message ? errors.username.message.toString() : ""}
											{...register("username", { required: "Username is required" })}
										/>
									</Box>

									<Box>
										<Typography
											variant="body2"
											fontWeight={600}
											color="text.primary"
											sx={{ mb: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
										>
											Password
										</Typography>

										<TextField
											required
											fullWidth
											variant="outlined"
											placeholder="Enter your password"
											type={showPassword ? "text" : "password"}
											autoComplete="off"
											InputProps={{
												sx: {
													borderRadius: "12px",
													backgroundColor: "rgba(102, 126, 234, 0.05)",
													"&:hover": {
														backgroundColor: "rgba(102, 126, 234, 0.08)"
													},
													"&.Mui-focused": {
														backgroundColor: "rgba(102, 126, 234, 0.1)"
													},
													fontSize: { xs: "0.875rem", sm: "1rem" }
												},
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															size="small"
															onClick={handleTogglePasswordVisibility}
															tabIndex={-1}
															sx={{
																color: "text.secondary",
																"&:hover": { color: "primary.main" }
															}}
														>
															{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
														</IconButton>
													</InputAdornment>
												)
											}}
											error={!!errors.password}
											helperText={errors.password && errors.password.message ? errors.password.message.toString() : ""}
											{...register("password", { required: "Password is required" })}
										/>
									</Box>

									<Box sx={{ mb: { xs: 1, sm: 0 } }}>
										<Captcha ref={captchaRef} />
									</Box>

									{authState.currentAction === "auth/loginUser" && authState.error ? (
										<Typography
											variant="body2"
											fontWeight={500}
											textAlign="center"
											color="var(--color-error-main)"
											sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
										>
											{authState.error}
										</Typography>
									) : null}

									<LoadingButton
										disableElevation
										variant="contained"
										color="primary"
										sx={{
											py: { xs: 1.25, sm: 1.5 },
											fontSize: { xs: "0.875rem", sm: "1rem" },
											mb: { xs: 1, sm: 0 }
										}}
										type="submit"
										loading={authState.currentAction === "auth/loginUser" && authState.loading}
									>
										Sign In
									</LoadingButton>

									<Box sx={{ textAlign: "center" }}>
										<Link
											onClick={handleOpenForgotPasswordDialog}
											sx={{
												color: "primary.main",
												textDecoration: "none",
												fontWeight: 500,
												cursor: "pointer",
												fontSize: { xs: "0.875rem", sm: "1rem" },
												"&:hover": {
													textDecoration: "underline"
												}
											}}
										>
											Forgot your password?
										</Link>
									</Box>
								</Box>
							</Paper>
						</Fade>
					</Box>
				</Box>
			</Container>

			<ForgotPasswordDialog open={showForgotPasswordDialog} onClose={handleCloseForgotPasswordDialog} />
		</Box>
	);
};

export default Login;
